import type {
  ConnectorInvocationStatusModel as ApiConnectorInvocationStatusResponse,
  InvocationObservationMetrics as ApiInvocationMetrics,
  PageConnectorInvocationStatusModel as ApiPageConnectorInvocationStatusResponse,
} from '@/adapters/apiv1.g';
import { extractPageModels } from '@/adapters/shared/extract-page-models';
import type {
  ConnectorInvocationStatus,
  ConnectorInvocationStatuses,
  InvocationApplyBatchesMetrics,
  InvocationCatalogApplyMetrics,
  InvocationCollectionMetrics,
  InvocationLifecycleMetrics,
  InvocationLifecycleStep,
  InvocationRSOSPlanMetrics,
  InvocationMetrics,
  InvocationReconciliationMetrics,
  InvocationSpacesMetrics,
  InvocationStatusType,
  InvocationWaitingMetrics,
} from '@/types/core/connector-invocation-status';

/**
 * Validate and normalize the status string to our internal enum.
 * @param statusStr - raw status string from API
 * @returns typed InvocationStatusType or 'queued' as default
 */
function normalizeStatus(statusStr?: string): InvocationStatusType {
  const normalized = statusStr?.toLowerCase();
  switch (normalized) {
    case 'queued':
      return 'queued';
    case 'requested':
      return 'requested';
    case 'running':
      return 'running';
    case 'reconciling':
      return 'reconciling';
    case 'waiting':
      return 'waiting';
    case 'updating':
      return 'updating';
    case 'completed':
      return 'completed';
    case 'failed':
      return 'failed';
    case 'stopped':
      return 'stopped';
    default:
      console.warn(`[connector] Unknown invocation status: "${statusStr}"`);
      return 'queued';
  }
}

/**
 * Parse ISO date string or numeric timestamp to Date object.
 * Returns undefined if input is falsy.
 * @param dateStr - ISO date string or numeric timestamp
 * @returns Date object or undefined
 */
function parseDate(dateStr?: string): Date | undefined {
  if (!dateStr) return undefined;
  try {
    return new Date(dateStr);
  } catch {
    return undefined;
  }
}

/**
 * Normalize numeric metric fields from the API (OpenAPI may emit number or string).
 */
function parseCount(count?: string | number): number | undefined {
  if (count === undefined || count === null) return undefined;
  if (typeof count === 'number') {
    return Number.isFinite(count) ? count : undefined;
  }

  const parsed = parseInt(count, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
}

function extractLifecycle(
  raw: ApiInvocationMetrics['lifecycle']
): InvocationLifecycleMetrics | undefined {
  const step = raw?.step;
  if (step === 'collecting' || step === 'processing' || step === 'updating') {
    return { step: step as InvocationLifecycleStep };
  }
  return undefined;
}

function extractMetrics(
  m: ApiInvocationMetrics | undefined
): InvocationMetrics | undefined {
  if (!m) return undefined;
  const out: InvocationMetrics = {};

  const lifecycle = extractLifecycle(m.lifecycle);
  if (lifecycle) {
    out.lifecycle = lifecycle;
  }

  if (m.collection) {
    const c: InvocationCollectionMetrics = {
      entitiesCollected: parseCount(m.collection.entities_collected),
      spaceNames: m.collection.space_names,
    };
    out.collection = c;
  }

  if (m.reconciliation) {
    const r = m.reconciliation;
    const rec: InvocationReconciliationMetrics = {};

    if (r.spaces) {
      const sp: InvocationSpacesMetrics = {
        total: r.spaces.total,
        completed: r.spaces.completed,
      };
      rec.spaces = sp;
    }

    rec.stagedTotal = parseCount(r.staged_total);

    if (r.waiting) {
      const waiting: InvocationWaitingMetrics = {
        active: r.waiting.active,
        reason: r.waiting.reason,
        space: r.waiting.space,
        attempts: parseCount(r.waiting.attempts),
      };
      rec.waiting = waiting;
    }

    if (r.rsos_plan) {
      const mp: InvocationRSOSPlanMetrics = {
        upsertCandidates: parseCount(r.rsos_plan.upsert_candidates),
        unchangedVsRemote: parseCount(r.rsos_plan.unchanged_vs_remote),
        deleteCandidates: parseCount(r.rsos_plan.delete_candidates),
      };
      rec.rsosPlan = mp;
    }

    if (r.catalog_apply) {
      const ca: InvocationCatalogApplyMetrics = {
        upsertsApplied: parseCount(r.catalog_apply.upserts_applied),
        upsertsNoop: parseCount(r.catalog_apply.upserts_noop),
        deletesApplied: parseCount(r.catalog_apply.deletes_applied),
        deletesSkippedAbsent: parseCount(
          r.catalog_apply.deletes_skipped_absent
        ),
      };
      rec.catalogApply = ca;
    }

    if (r.apply_batches) {
      const ab: InvocationApplyBatchesMetrics = {
        total: r.apply_batches.total,
        completed: r.apply_batches.completed,
      };
      rec.applyBatches = ab;
    }

    rec.collectionCompletedAt = parseDate(r.collection_completed_at);
    rec.updatingStartedAt = parseDate(r.updating_started_at);
    rec.planningCompletedAt = parseDate(r.planning_completed_at);
    rec.catalogApplyStartedAt = parseDate(r.catalog_apply_started_at);
    if (typeof r.updating_phase === 'string' && r.updating_phase) {
      rec.updatingPhase = r.updating_phase;
    }
    if (typeof r.planning_space === 'string' && r.planning_space) {
      rec.planningSpace = r.planning_space;
    }
    rec.reconciliationCompletedAt = parseDate(r.reconciliation_completed_at);

    out.reconciliation = rec;
  }

  if (!out.collection && !out.reconciliation && !out.lifecycle) {
    return undefined;
  }

  return out;
}

/**
 * Transform a single invocation status from API shape to UI domain shape.
 */
export function extractInvocationStatus(
  model: ApiConnectorInvocationStatusResponse
): ConnectorInvocationStatus {
  return {
    runSequence: parseCount(model.run_sequence),
    status: normalizeStatus(model.status),
    requestedAt: parseDate(model.requested_at),
    startedAt: parseDate(model.started_at),
    completedAt: parseDate(model.completed_at),
    failedAt: parseDate(model.failed_at),
    stoppedAt: parseDate(model.stopped_at),
    message: model.message,
    processedCount: parseCount(model.processed_count),
    metrics: extractMetrics(model.metrics),
  };
}

/**
 * Transform a paginated response of invocation statuses from API to UI shape.
 */
export function extractInvocationStatuses(
  response: ApiPageConnectorInvocationStatusResponse
): ConnectorInvocationStatuses {
  const items = extractPageModels<ApiConnectorInvocationStatusResponse>(
    response
  ).map(extractInvocationStatus);

  return {
    items: items as ReadonlyArray<ConnectorInvocationStatus>,
    next: response?.next ?? null,
    prev: response?.prev ?? null,
  };
}
